import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteTodoTaskAction } from '../actions/deleteTodoTask.action';
import { FlowTask } from '../interfaces/flowTask.interface';
import { useAppDispatch } from '../../../redux/hooks/redux';
import { fetchBenefits } from '../../../permissions/benefits/redux/benefits.thunks';

export const useDeleteTodoTask = () => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  return useMutation({
    mutationFn: ({ taskId }: { taskId: string; kanbanCategoryUid: string }) =>
      deleteTodoTaskAction(taskId),

    onMutate: async ({ taskId }) => {
      // 1. Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['todo-tasks'] });

      // 2. Snapshot the previous value
      const previousTasks = queryClient.getQueryData<FlowTask[]>(['todo-tasks']);

      // 3. Optimistically update to the new value
      if (previousTasks) {
        queryClient.setQueryData<FlowTask[]>(
          ['todo-tasks'],
          previousTasks.filter((task) => task.uid !== taskId)
        );
      }

      // 4. Return context with snapshot
      return { previousTasks };
    },

    onError: (err, variables, context) => {
      // Rollback if there was an error
      if (context?.previousTasks) {
        queryClient.setQueryData(['todo-tasks'], context.previousTasks);
      }
    },

    onSuccess: (_, { taskId }) => {
      // Double check the global cache
      dispatch(fetchBenefits());
      queryClient.setQueryData<FlowTask[]>(['todo-tasks'], (oldTasks) => {
        if (!oldTasks) return [];
        return oldTasks.filter((task) => task.uid !== taskId);
      });
    },

    onSettled: () => {
      // Broad invalidation
      queryClient.invalidateQueries({ queryKey: ['todo-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['kanban-categories'] });
      queryClient.invalidateQueries({ queryKey: ['kanban-pages'] });

      // Also invalidate any potential scoped queries just in case
      queryClient.invalidateQueries({ queryKey: ['todo-tasks'], exact: false });
    },
  });
};
